import { Injectable, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import { IdleTimeoutService } from "../idle-timeout/idle-timeout-service";
import { UserInfoService } from "../auth-service/user-info-service";
import { AlertController } from "@ionic/angular";
import { AuthService } from "../auth-service/auth.service";
import { TranslatorService } from "../translator-service/translator.service";
import { TreatmentService } from "../treatment-service/treatment.service";
import { PractitionerService } from "../practitioner-service/practitioner.service";
import { environment } from "src/environments/environment";

interface IdleTimeoutSubscriber {
    name: string;
    subscriptionType: 'timeout' | 'warning' | 'both';
    registeredAt: Date;
}

type IdleWarningAlert = Awaited<ReturnType<AlertController['create']>>;

@Injectable({
    providedIn: 'root'
})
export class SessionService implements OnDestroy {

    private idleTimeoutSubscriptions: Subscription[] = [];
    private warningAlertPresent: boolean = false;
    private warningAlert: IdleWarningAlert | null = null;
    private idleTimeoutSubscribers: Map<string, IdleTimeoutSubscriber> = new Map();
    private idleLogoutInProgress = false;

    constructor(
        private authService: AuthService,
        private alertController: AlertController,
        private translationService: TranslatorService,
        private treatmentService: TreatmentService,
        private practitionerService: PractitionerService,
    ) { }

    public initializeSessionService(): void {
        this.subscribeToIdleTimeout();
    }

    /**
     * Starts idle watching with environment timeouts. Idempotent — safe to call from
     * OTP login, session restore (landing/web), and LoginGuard.
     */
    public ensureIdleTimeoutInitialized(): void {
        IdleTimeoutService.init({
            debounceMs: 300,
            attachDomListeners: true,
            timeoutMs: environment.idleTimeoutMs,
            warningTimeoutMs: environment.idleWarningTimeoutMs,
        });
    }

    /**
     * Registers a component/service that subscribes to idle timeout events.
     * This allows SessionService to track active subscribers and prevent logout
     * if there are active subscribers handling the timeout.
     * 
     * @param subscriberName - Unique name/identifier for the subscriber (e.g., component/service name)
     * @param subscriptionType - Type of subscription: 'timeout', 'warning', or 'both'
     */
    public registerIdleTimeoutSubscriber(
        subscriberName: string,
        subscriptionType: 'timeout' | 'warning' | 'both' = 'timeout'
    ) {
        if (this.idleTimeoutSubscribers.has(subscriberName)) {
            console.warn(`Subscriber ${subscriberName} is already registered. Updating registration.`);
        }

        this.idleTimeoutSubscribers.set(subscriberName, {
            name: subscriberName,
            subscriptionType,
            registeredAt: new Date()
        });
    }

    /**
     * Unregisters a component/service that was subscribing to idle timeout events.
     * This should be called when the component unsubscribes or is destroyed.
     * 
     * @param subscriberName - Unique name/identifier for the subscriber to remove
     */
    public unregisterIdleTimeoutSubscriber(subscriberName: string): void {
        if (this.idleTimeoutSubscribers.delete(subscriberName)) {
        }
    }

    /**
     * Checks if there are any active subscribers to idle timeout events.
     * 
     * @returns true if there are active subscribers, false otherwise
     */
    public hasActiveSubscribers(): boolean {
        return this.idleTimeoutSubscribers.size > 0;
    }

    /**
     * Gets the list of active subscriber names.
     * 
     * @returns Array of subscriber names
     */
    public getActiveSubscribers(): string[] {
        return Array.from(this.idleTimeoutSubscribers.keys());
    }

    private subscribeToIdleTimeout(): void {
        // Warning is UI-only; logout is driven solely by idleTimeoutMs via onTimeout$.
        const warningSub = IdleTimeoutService.onWarningTimeout$.subscribe(async () => {
            if (this.idleLogoutInProgress) {
                return;
            }

            const userInfo = await UserInfoService.getUserInfo();
            if (!userInfo || this.warningAlertPresent) {
                return;
            }

            await this.showLogoutWarning();
        });

        const timeoutSub = IdleTimeoutService.onTimeout$.subscribe(async () => {
            if (this.idleLogoutInProgress) {
                return;
            }

            const userInfo = await UserInfoService.getUserInfo();
            if (!userInfo) {
                return;
            }

            await this.logoutAfterPersistingEncounter('Idle timeout', {
                // Timeout already fired — TreatmentPage (if open) is handling onTimeout$ concurrently.
                triggerTimeoutHandlers: false,
            });
        });

        this.idleTimeoutSubscriptions.push(warningSub, timeoutSub);
    }

    private async showLogoutWarning(): Promise<void> {
        if (this.warningAlertPresent || this.idleLogoutInProgress) {
            return;
        }

        this.warningAlertPresent = true;

        const alert = await this.alertController.create({
            cssClass: 'custom-idle-timeout-alert',
            header: this.translationService.translateText('idle-timeout.warning-title') || 'Session Timeout Warning',
            message: this.translationService.translateText('idle-timeout.warning-message') ||
                'You will be automatically logged out due to inactivity. Would you like to extend your session?',
            buttons: [
                {
                    text: this.translationService.translateText('idle-timeout.logout-button') || 'Log Out',
                    role: 'cancel',
                    handler: () => {
                        // Ionic awaits async handlers; kick off logout without blocking dismiss.
                        void this.logoutAfterPersistingEncounter(
                            'User selected logout from idle timeout warning',
                            { triggerTimeoutHandlers: true },
                        );
                        return true;
                    }
                },
                {
                    text: this.translationService.translateText('idle-timeout.extend-button') || 'Extend Session',
                    handler: () => {
                        this.warningAlertPresent = false;
                        this.warningAlert = null;
                        IdleTimeoutService.reset();
                        return true;
                    }
                }
            ],
            backdropDismiss: false
        });

        this.warningAlert = alert;
        await alert.present();

        alert.onDidDismiss().then(() => {
            if (this.warningAlert === alert) {
                this.warningAlert = null;
                this.warningAlertPresent = false;
            }
        });
    }

    private async dismissWarningAlert(): Promise<void> {
        const alert = this.warningAlert;
        this.warningAlert = null;
        this.warningAlertPresent = false;
        if (alert) {
            try {
                await alert.dismiss();
            } catch {
                // Already dismissed
            }
        }
    }

    /**
     * Save any in-progress encounter (after token refresh if needed), then log out.
     * Used by idle timeout and by app-version change logout.
     * Idle watching is deactivated for the duration so DOM noise from save/navigation
     * cannot reset timers or re-show the warning.
     */
    public async logoutPersistingCurrentTreatment(
        reason: string,
        options?: { triggerTimeoutHandlers?: boolean },
    ): Promise<void> {
        await this.logoutAfterPersistingEncounter(reason, options);
    }

    /**
     * Save any in-progress encounter (after token refresh if needed), then log out.
     * Idle watching is deactivated for the duration so DOM noise from save/navigation
     * cannot reset timers or re-show the warning.
     */
    private async logoutAfterPersistingEncounter(
        reason: string,
        options?: { triggerTimeoutHandlers?: boolean },
    ): Promise<void> {
        if (this.idleLogoutInProgress) {
            return;
        }
        this.idleLogoutInProgress = true;

        // Stop idle/warning timers and DOM activity resets for the rest of this flow.
        IdleTimeoutService.setActive(false);
        await this.dismissWarningAlert();

        try {
            await this.refreshTokenIfCurrentEncounterPending();

            if (options?.triggerTimeoutHandlers && this.hasActiveSubscribers()) {
                // Manual logout from warning while still on treatment — notify page to save.
                await this.waitForTimeoutHandlers();
            }

            if (this.hasActiveSubscribers()) {
                await this.waitForSubscribersToComplete();
                if (this.hasActiveSubscribers()) {
                    console.warn(
                        `Idle logout: subscribers still active after wait, forcing logout. Remaining: ${this.getActiveSubscribers().join(', ')}`,
                    );
                }
            }

            await this.saveCurrentEncounterIfPending();
            await this.authService.logout(reason);
        } catch (error) {
            console.error('Idle logout failed; forcing logout', error);
            try {
                await this.authService.logout(reason);
            } catch (logoutError) {
                console.error('Forced logout also failed', logoutError);
                this.idleLogoutInProgress = false;
                IdleTimeoutService.setActive(true);
            }
        }
    }

    private async hasPendingSavableEncounter(): Promise<boolean> {
        const patientId = this.treatmentService.getActivePatientId();
        if (!patientId) {
            return false;
        }

        const encounter = await this.treatmentService.getCurrentEncounter(patientId, {
            allowWhenUninitialized: true,
        });
        return !!encounter && TreatmentService.encounterHasSavableTreatments(encounter);
    }

    /**
     * Refresh only when the access token TTL is invalid, so a refresh API failure
     * cannot block encounter save while a still-valid token is available.
     */
    private async refreshTokenIfCurrentEncounterPending(): Promise<void> {
        const hasEncounter =
            (await this.hasPendingSavableEncounter()) ||
            this.getActiveSubscribers().includes('TreatmentPage') ||
            this.getActiveSubscribers().includes('TreatmentAdjustComponent');

        if (!hasEncounter) {
            return;
        }

        const tokenExpired = await this.authService.tokenExpired();
        if (!tokenExpired) {
            return;
        }

        try {
            await this.authService.refreshToken({ logoutOnFailure: false });
        } catch (error) {
            console.warn('Idle logout: token refresh before encounter save failed', error);
        }
    }

    /**
     * Fallback encounter save when TreatmentPage did not persist (or was not open).
     */
    private async saveCurrentEncounterIfPending(): Promise<void> {
        const patientId = this.treatmentService.getActivePatientId();
        if (!patientId) {
            return;
        }

        const encounter = await this.treatmentService.getCurrentEncounter(patientId, {
            allowWhenUninitialized: true,
        });
        if (!encounter || !TreatmentService.encounterHasSavableTreatments(encounter)) {
            return;
        }

        try {
            const facility = await this.practitionerService.getSelectedFacility();
            if (!facility?.id) {
                console.warn('Idle logout: cannot save encounter — no selected facility');
                return;
            }
            const durable = await this.treatmentService.completeCachedEncounter(
                patientId,
                facility.id,
            );
            if (durable) {
                await this.treatmentService.clearCurrentEncounter(patientId);
            }
        } catch (error) {
            console.error('Idle logout: failed to save current encounter before logout', error);
        }
    }

    /**
     * Waits for active subscribers to complete their timeout handling.
     * This gives subscribers time to finish their operations before logout.
     * 
     * @param maxWaitMs - Maximum time to wait for subscribers to complete (default: 30 seconds)
     */
    private async waitForSubscribersToComplete(maxWaitMs: number = 30000): Promise<void> {
        const startTime = Date.now();
        const checkInterval = 500; // Check every 500ms

        while (this.hasActiveSubscribers() && (Date.now() - startTime) < maxWaitMs) {
            await new Promise(resolve => setTimeout(resolve, checkInterval));
        }

        if (this.hasActiveSubscribers()) {
            console.warn(`Wait timeout reached. Remaining subscribers: ${this.getActiveSubscribers().join(', ')}`);
        }
    }

    /**
     * Waits for timeout handlers (like completeTreatment) to finish before proceeding with logout.
     * This ensures data is saved before the user is logged out.
     * Uses IdleTimeoutService's triggerTimeoutAndWait method to properly wait for async operations.
     */
    private async waitForTimeoutHandlers(): Promise<void> {
        try {
            // Use the service method that properly waits for handlers to complete
            // Wait up to 30 seconds for async operations to finish (configurable)
            // This ensures completeTreatment has time to save data even with slow network
            await IdleTimeoutService.triggerTimeoutAndWait(30000); // 30 second max wait
        } catch (error) {
            console.error('Error waiting for timeout handlers:', error);
            // Continue with logout even if there's an error
        }
    }

    ngOnDestroy(): void {
        this.idleTimeoutSubscriptions.forEach(sub => sub.unsubscribe());
    }
}
